// Making one lesson into something else, for one date only.
//
// Taylor's case, in her words: "If a client books the 11 AM private lesson,
// the trainer could later convert it to a semi-private, specify the discipline
// and level, and invite another compatible rider."
//
// The whole reason this was impossible until now is that capacity and lesson
// type lived on the recurring block, so making Sarah's 11 AM a semi-private
// would have made EVERY Wednesday a semi-private. It writes a slot override
// instead: one row, one date, and next Wednesday is a private again. That
// sentence is on the screen, because a trainer has no way to know it otherwise
// and the wrong guess is the expensive one.
//
// WHAT IT DELIBERATELY DOES NOT DO
//
// It does not tell Sarah. Converting a private into a shared lesson changes
// what she bought, and stallSIDE has no billing for that conversation to land
// in — so inventing half a pricing system inside the scheduler would be worse
// than saying plainly that the call is hers to make. The dialog names her and
// stops.
//
// It does not pick the second rider either. Eligibility by level is a module
// of its own; opening the place is this screen's job, and filling it is the
// booking page's.

import { useState } from 'react';
import { fmtDate } from '../../lib/dates';
import Stepper from '../ui/Stepper';

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function ConvertSlot({ slot, date, types = [], busy, error, onCancel, onSave }) {
  const riders = slot.riders ?? [];
  const fromCapacity = slot.capacity ?? 1;

  const [capacity, setCapacity] = useState(String(fromCapacity));
  // Empty means "leave the type alone", which is not the same as "no type".
  const [typeId, setTypeId] = useState(slot.lessonTypeId ?? '');
  const [note, setNote] = useState(slot.overrideNote ?? '');

  const n = Number(capacity);
  const valid = Number.isInteger(n) && n >= 1 && n <= 20;
  // Shrinking below the people already in it is the one change that cannot be
  // honoured, so it is refused rather than accepted and quietly ignored.
  const tooSmall = valid && n < riders.length;
  const changed =
    n !== fromCapacity || (typeId || '') !== (slot.lessonTypeId ?? '') || note.trim() !== (slot.overrideNote ?? '');

  const chosen = types.find((t) => t.id === typeId) ?? null;
  const opening = valid ? Math.max(0, n - riders.length) : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || tooSmall || !changed || busy) return;
          onSave({
            capacity: n,
            lessonTypeId: typeId || null,
            note: note.trim() || null,
          });
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Change this lesson"
        className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">Change this lesson</h2>
            <p className="text-sm text-ink-500">
              {fmtDate(date, 'dayMedium')} at {fmtTime(slot.startTime)}
            </p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0">
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Said first and in the plainest words available, because it is the
              fact a trainer cannot discover any other way. */}
          <p className="rounded-md border border-hunter/25 bg-hunter/5 px-3 py-2 text-sm text-hunter">
            This changes <strong>{fmtDate(date, 'dayMedium')} only</strong>. Every other{' '}
            {fmtDate(date, 'weekdayLong')} stays as it is.
          </p>

          {riders.length > 0 && (
            <div>
              <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
                Already booked
              </span>
              <ul className="text-sm">
                {riders.map((r) => (
                  <li key={r.lessonId}>
                    <span className="font-medium">{r.name}</span>
                    {r.horseName && <span className="text-ink-500"> · {r.horseName}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
              How many riders
            </span>
            <Stepper
              value={capacity}
              onChange={setCapacity}
              min={1}
              max={20}
              disabled={busy}
              label="riders in this lesson"
            />
            <p className="mt-1.5 text-xs text-ink-500">
              {tooSmall
                ? ''
                : opening === 0
                  ? n === 1
                    ? 'A private lesson.'
                    : 'Full — no places left to fill.'
                  : `${opening} ${opening === 1 ? 'place' : 'places'} open for somebody else to book.`}
            </p>
            {tooSmall && (
              <p className="mt-1.5 text-sm text-rose-700">
                {riders.length} {riders.length === 1 ? 'rider is' : 'riders are'} already booked
                into this. Cancel {riders.length === 1 ? 'that lesson' : 'one of them'} before
                making it smaller.
              </p>
            )}
          </div>

          <label className="block">
            <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
              What kind of lesson
            </span>
            <select className="input" value={typeId} disabled={busy}
              onChange={(e) => setTypeId(e.target.value)}>
              <option value="">Leave it as it is</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {chosen && chosen.id !== (slot.lessonTypeId ?? '') && (
              <span className="block text-xs text-ink-400 mt-1">
                Just this one. The usual {chosen.defaultMinutes} minutes and{' '}
                {chosen.defaultCapacity === 1 ? 'one rider' : `${chosen.defaultCapacity} riders`}{' '}
                are not applied — the hours here stay as they are.
              </span>
            )}
          </label>

          <label className="block">
            <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
              Why (optional)
            </span>
            <input className="input" value={note} disabled={busy} maxLength={120}
              placeholder="Sarah asked for a buddy"
              onChange={(e) => setNote(e.target.value)} />
          </label>

          {/* The pushback, on the screen rather than in a document. */}
          {riders.length > 0 && n > fromCapacity && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {riders[0].name} booked {fromCapacity === 1 ? 'a private lesson' : 'this'}. Opening
              it up changes what {riders.length === 1 ? 'she' : 'they'} booked, and stallSIDE
              won&apos;t tell {riders.length === 1 ? 'her' : 'them'} or adjust any pricing — have
              that conversation yourself first.
            </p>
          )}

          {error && <p className="text-sm text-rose-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-ink-100">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy || !valid || tooSmall || !changed}>
            {busy ? 'Saving…' : `Change ${fmtDate(date, 'short')} only`}
          </button>
        </div>
      </form>
    </div>
  );
}
